import { useHistory, useLocation } from "react-router";
import React, { FunctionComponent, useEffect } from "react";
import { Box, Link, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import classNames from "classnames";
import ReactToPrint from "react-to-print";
import { connect } from "react-redux";
import Barcode from "react-barcode";
import "./PrintBill.scss";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { t, tl } from "../../../components/translate";
import { registerPrint } from "../../../actions/bill";
import {
  BillStatus,
  BillType,
  DocumentTypes,
  PrintTemplate
} from "../../../interfaces/BillInterfaces";
import { dateWithZeroBeforeSingleDigits } from "../../../helpers/formatters";
import { isOldFormatBill } from "../../../actions/helpers/billHelper";
import DescribeBillV1 from "./BillDescriptionV1";
import DescribeBillV2 from "./BillDescriptionV2";
import usePrintStyles from "../../../hooks/usePrintStyles";
import { getSectionStyle } from "../../Lab/LabPrint/LabPrintFunctions";
import LabPrintLetterHead from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import { BillClientInfo, checkWalkInCustomer, InfoField, scaleFont } from "./BillPrintHelpers";
import LabPrintLetterHeadCentered from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import usePrintShortcut from "../../../hooks/usePrintShortcut";
import useLabIdsFromBills from "../../../hooks/useLabIdsFromBills";
import { disclaimerText } from "../Editor/PrintPreview/DraftBill";
import { getCustomerNumber } from "../../Client/ClientList";
import QrFromUrl from "../../../components/Qr/QrFromUrl";
import { DataComment } from "../../Lab/LabPrint/Templates/DefaultTemplate/DefaultTemplateCore";
import { hasValidRichTextRemark } from "../Editor/BilllEditorActions";
import CustomHeader from "../../Lab/LabPrint/CustomHeader";
import { rupeeDisplay } from "../../../helpers/rupee";

// @ts-expect-error - image
import logo from "../../../../assets/images/poweredByOkhati.png";

interface PrintBillProps {
  showDobOnPrint?: boolean;
  hidePaymentMethodOnPrint?: boolean;
  buttonText?: string;
  billId: number;
  resourceCentre: ResourceCentre;
  billData: BillType;
  customerNumber: string | null;
  sendPrintInfo: (id) => void;
  SPs: ServiceProvider;
  useBordered: boolean;
  labelStyle?: React.CSSProperties;
  forceTemplate?: string | null;
  clientBalance: number;
  addPrintCount?: boolean;
  onPrintClick?: () => void;
  isForPreview?: boolean;
  labTestUrl?: string;
  printAsEstimate?: boolean;
}

export const getPrintTitle = ({
  billStatus,
  billType,
  printAsEstimate,
  customTitle = ""
}: {
  billStatus: BillStatus;
  billType: DocumentTypes;
  printAsEstimate: boolean;
  customTitle?: string;
}): string => {
  if (printAsEstimate) {
    return `${t("billing.title.printAsEstimate")}`;
  }

  // title based on bill type
  if (billType === DocumentTypes.CREDITNOTE) {
    return `${t("billing.creditNote")}`;
  }

  // title based on bill status
  if (billStatus === BillStatus.draft) {
    return `${t("billing.draft")}`;
  }

  if (billStatus === BillStatus.cancelled) {
    return `${t("billing.invoice")} (${t("billing.cancelled")})`;
  }

  if (billStatus === BillStatus.proformaDraft) {
    return `${t("billing.proformaDraft")}`;
  }

  // custom title
  if (customTitle) {
    return customTitle;
  }

  return `${t("billing.invoice")}`;
};

const PrintBill: FunctionComponent<PrintBillProps> = ({
  billId,
  billData,
  buttonText = "Print",
  resourceCentre,
  sendPrintInfo,
  SPs,
  labelStyle = {},
  useBordered = false,
  forceTemplate,
  clientBalance,
  addPrintCount = true,
  onPrintClick,
  showDobOnPrint = false,
  hidePaymentMethodOnPrint = false,
  isForPreview = false,
  labTestUrl,
  printAsEstimate = false
}) => {
  const history = useHistory();
  const labIdsForBill = useLabIdsFromBills(billData);
  const location = useLocation<{ openPrintWindow?: boolean }>();

  // refs
  const printRef = React.useRef<HTMLDivElement>(null);
  const reactToPrintRef = React.useRef<HTMLButtonElement>(null);

  // states
  const [isPrinting, setIsPrinting] = React.useState(false);

  // variables
  const showDueOnBillPrint = localStorage.getItem("showDueOnBillPrint") === "true";
  const billDue = (billData.summary?.totalAmount || 0) - (billData.paymentInfo.paidAmount || 0);
  const showLetterhead =
    resourceCentre?.settings?.printSettings.billPrintSettings?.includeLetterhead;
  const clinicNameFontScale = resourceCentre?.settings?.printSettings.clinicNameFontScale;
  const centralizedHeader: boolean = resourceCentre?.settings?.printSettings.centralizedHeader;
  const showFooter = resourceCentre?.settings?.printSettings.billPrintSettings?.includeFooter;
  const isVatRegistered = resourceCentre?.settings?.billingSettings.vatRegistered;

  const hideFooterCompletely =
    resourceCentre.settings.printSettings.billPrintSettings.hideFooterLogoCompletely;

  const showReferrer = resourceCentre?.settings?.printSettings.includeReferrer;
  const headerColor: string =
    (resourceCentre?.settings.printSettings.labPrintSettings.enableColor &&
      resourceCentre?.settings.printSettings.labPrintSettings.color) ||
    null;
  const panNo = resourceCentre?.settings?.billingSettings?.panNo;
  const customTitle = resourceCentre?.settings?.printSettings?.billPrintSettings?.customTitle;
  const customSubtitle = resourceCentre?.settings?.printSettings?.billPrintSettings?.customSubtitle;
  const customHeader = resourceCentre?.settings?.billingSettings?.customHeaderImage;
  const hideRcLogo = Boolean(customTitle);

  let letterHeadComponent = <Box />;
  if (showLetterhead && !customHeader) {
    if (centralizedHeader) {
      letterHeadComponent = (
        <LabPrintLetterHeadCentered
          resourceCentre={{
            ...resourceCentre,
            panNo,
            subtitle: customSubtitle
          }}
          titleFontScale={clinicNameFontScale}
          hideRcLogo={hideRcLogo}
        />
      );
    } else {
      letterHeadComponent = (
        <LabPrintLetterHead
          resourceCentre={{
            ...resourceCentre,
            panNo,
            subtitle: customSubtitle
          }}
          titleFontScale={clinicNameFontScale}
          hideRcLogo={hideRcLogo}
        />
      );
    }
  }

  const bankPaperPrintStyles = usePrintStyles({
    pageStyle: `size: 13cm 24.5cm; margin: 0mm; margin-bottom: 12pt;
          counter-increment: page;
            @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 8pt;
          }`
  });

  const defaultPrintStyles = usePrintStyles({
    pageStyle: `size: A4; margin: 0mm;margin-bottom: 12pt;
          counter-increment: page;
            @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 8pt;
          }`
  });

  const applyCustomPrintStyle = React.useCallback(() => {
    if (forceTemplate === PrintTemplate.BankPaper) {
      bankPaperPrintStyles.applyPrintStyles();
    } else {
      defaultPrintStyles.applyPrintStyles();
    }
  }, [bankPaperPrintStyles, defaultPrintStyles, forceTemplate]);

  // effects
  useEffect(() => {
    if (
      location.state?.openPrintWindow &&
      (!billData?.document?.createdLabTestIds?.length ||
        (billData?.document?.createdLabTestIds?.length && Boolean(labTestUrl)))
    ) {
      applyCustomPrintStyle();
      reactToPrintRef?.current?.click();

      history.replace({ ...location, state: { ...location.state, openPrintWindow: false } });
    }
  }, [
    labTestUrl,
    billData?.document?.createdLabTestIds?.length,
    location.state,
    location,
    applyCustomPrintStyle,
    history
  ]);

  usePrintShortcut(reactToPrintRef, applyCustomPrintStyle);

  if (!billData || !billData.document) {
    return null;
  }

  return (
    <>
      <ReactToPrint
        onAfterPrint={() => {
          // after cancelling print, shortcut works only after clicking on window.
          window.focus();
          if (addPrintCount) {
            sendPrintInfo(billId);
          }
          setIsPrinting(false);
        }}
        onBeforePrint={() => {
          if (onPrintClick) onPrintClick();
        }}
        trigger={() => (
          <Box>
            <Tooltip arrow title="Alt + P / Ctrl + P">
              <Link
                type="submit"
                className="actionBtn"
                component="button"
                onMouseEnter={applyCustomPrintStyle}
                ref={reactToPrintRef}
                onClick={() => {
                  if (isPrinting) return;
                  setIsPrinting(true);
                }}
                style={{ textDecoration: "none", padding: "5px 15px" }}
              >
                <Typography component="span" style={labelStyle}>
                  <Box className="actionBtnLabel" component="span">
                    {buttonText || "Print"}
                  </Box>
                </Typography>
              </Link>
            </Tooltip>
          </Box>
        )}
        content={() => printRef.current}
      />
      <iframe title="printContents" className="displayContents">
        <div ref={printRef}>
          <Box
            component="table"
            width="100%"
            className={classNames({ "water-marks": billData.isDraft })}
            waterMarkText={printAsEstimate || !isForPreview ? "" : disclaimerText}
          >
            <tfoot>
              <tr>
                <td>
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <div className="billFooterOffset" />
                </td>
              </tr>
            </tfoot>

            <thead>
              {!showLetterhead && (
                <tr>
                  <td>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <div style={{ height: "3.5cm" }} />
                  </td>
                </tr>
              )}
              {showLetterhead && (
                <tr>
                  <td>
                    {customHeader ? (
                      <CustomHeader imageUrl={customHeader} />
                    ) : (
                      <Box style={{ ...getSectionStyle(headerColor).headerStyle }}>
                        {letterHeadComponent}
                      </Box>
                    )}
                  </td>
                </tr>
              )}
            </thead>
            <tbody>
              <tr>
                <td>
                  <Box style={{ ...getSectionStyle(headerColor).bodyStyle }}>
                    <Box
                      component="div"
                      display="flex"
                      marginBottom="0.8cm"
                      width="100%"
                      justifyContent="center"
                    >
                      <Typography style={{ textAlign: "center" }}>
                        <Box
                          fontSize="0.45cm"
                          marginTop="0.5cm"
                          marginBottom="0.3cm"
                          fontWeight={400}
                          style={{ textTransform: "uppercase" }}
                        >
                          {getPrintTitle({
                            billStatus: billData.status,
                            billType: billData.type,
                            printAsEstimate,
                            customTitle
                          })}
                          {!printAsEstimate && !isForPreview && billData?.printCount
                            ? ` (COPY of Original ${billData?.printCount})`
                            : ""}
                        </Box>
                      </Typography>
                    </Box>

                    <Box
                      component="div"
                      display="flex"
                      marginBottom="1cm"
                      justifyContent="space-between"
                      borderTop="1px solid #bbb"
                      borderRight={useBordered ? "1px solid #bbb" : ""}
                      borderBottom={useBordered ? "1px solid #bbb" : ""}
                      borderLeft={useBordered ? "1px solid #bbb" : ""}
                      p="0.3cm"
                    >
                      <Box component="div" width="50%">
                        {checkWalkInCustomer(billData.client, "isWalkInCustomer") && (
                          <BillClientInfo
                            client={billData.client}
                            customerNumber={getCustomerNumber(billData.customer?.customerNumber)}
                            showDobOnPrint={showDobOnPrint}
                            isBordered={useBordered}
                          />
                        )}
                        {labIdsForBill && (
                          <InfoField label={tl("billing.labId")} data={<>{labIdsForBill}</>} />
                        )}
                        <Barcode
                          value={billId}
                          width={2}
                          displayValue={false}
                          fontSize={5}
                          height={20}
                          margin={2}
                          font="arial"
                        />
                      </Box>

                      <Box component="div" width={forceTemplate === "bankPaper" ? "46%" : "40%"}>
                        {billData.ipd && (
                          <InfoField
                            label={<>IPD No.</>}
                            data={<>{billData.ipd.ipdNumber || ""}</>}
                          />
                        )}
                        {billData.document.bed?.name && (
                          <InfoField
                            label={<>Bed No.</>}
                            data={<>{billData.document.bed.name}</>}
                          />
                        )}
                        {!printAsEstimate && (
                          <InfoField
                            label={
                              billData.type === DocumentTypes.CREDITNOTE
                                ? tl("billing.creditNoteNumber")
                                : tl("billnumber")
                            }
                            data={<>{billData.billNumber}</>}
                          />
                        )}
                        {billData.type === DocumentTypes.CREDITNOTE && (
                          <InfoField
                            label={tl("billing.creditNoteForBill")}
                            data={<>{billData.related?.billNumber}</>}
                          />
                        )}
                        <InfoField
                          label={tl("billing.issueDate")}
                          data={
                            <>
                              {dateWithZeroBeforeSingleDigits(
                                convertADtoBS(moment(billData.issueDate)).formatted
                              )}{" "}
                              {moment(billData.issueDate).format("h:mm a")}
                            </>
                          }
                        />
                        <InfoField
                          label={tl("billing.dateAD")}
                          data={
                            <>
                              {dateWithZeroBeforeSingleDigits(
                                moment(billData.issueDate).format("YYYY/MM/DD")
                              )}{" "}
                            </>
                          }
                        />
                        {billData.document.referredBy && (
                          <InfoField
                            label={tl("booking.referredBy")}
                            data={<>{billData.document.referredBy.toUpperCase()}</>}
                          />
                        )}
                        {billData.document.extraReferrer && (
                          <InfoField
                            label="Extra Referrer"
                            data={<>{billData.document.extraReferrer.name.toUpperCase()}</>}
                          />
                        )}
                        {billData.ipd?.doctorInCharge && (
                          <InfoField
                            label="Consulted by"
                            data={
                              <>
                                {`${billData.ipd.doctorInCharge?.firstName || ""} ${
                                  billData.ipd.doctorInCharge?.lastName || ""
                                }`}
                              </>
                            }
                          />
                        )}
                        {billData.insuranceNumber && (
                          <InfoField
                            label={tl("billing.insuranceNumber")}
                            data={<>{billData.insuranceNumber} </>}
                          />
                        )}
                        {billData.claimNumber && (
                          <InfoField
                            label={tl("billing.claimNumber")}
                            data={<>{billData.claimNumber} </>}
                          />
                        )}
                        {billData?.document?.ssf?.id && (
                          <InfoField
                            label={tl("billing.ssid")}
                            data={<>{billData.document.ssf.id}</>}
                          />
                        )}

                        {billData?.document?.ssf?.id && billData?.document?.ssf?.scheme?.value && (
                          <InfoField
                            label={tl("billing.scheme")}
                            data={
                              <>
                                {`${billData.document.ssf.scheme.label} (${billData.document.ssf.scheme.value})`}
                              </>
                            }
                          />
                        )}

                        {billData?.document?.ssf?.id &&
                          billData?.document?.ssf?.subProduct?.value && (
                            <InfoField
                              label={tl("billing.subScheme")}
                              data={
                                <>
                                  {`${billData.document.ssf.subProduct.label} (${billData.document.ssf.subProduct.value})`}
                                </>
                              }
                            />
                          )}
                      </Box>
                    </Box>

                    {/* check the version and load bill content accordingly,
                    may use switch case if third version comes */}
                    {isOldFormatBill(billData.document.version) ? (
                      <DescribeBillV1 billData={billData} SPs={SPs} />
                    ) : (
                      <DescribeBillV2
                        isVatRegistered={isVatRegistered}
                        billData={billData}
                        SPs={SPs}
                        relatedSettings={{
                          showReferrer,
                          isBordered: useBordered,
                          headerColor
                        }}
                        forceTemplate={forceTemplate}
                        clientBalance={clientBalance}
                        hidePaymentMethodOnPrint={hidePaymentMethodOnPrint}
                      />
                    )}
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>
          <div className="billFooterPos" style={{ width: "100%" }}>
            {/* previous due amount */}
            {showDueOnBillPrint && clientBalance < 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "5px",
                  padding: "0 10mm",
                  gap: "5px"
                }}
              >
                <Typography fontSize={scaleFont("0.3cm")}>
                  {tl("billing.previousTotalDue")}:
                </Typography>
                <Typography fontSize={scaleFont("0.3cm")}>
                  {rupeeDisplay(Math.abs(clientBalance))}
                </Typography>
              </Box>
            )}

            {/* more visible remark */}
            {hasValidRichTextRemark(billData?.document?.remarks) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "10px",
                  padding: "5px 10mm",
                  gap: "5px"
                }}
              >
                <Typography fontSize={scaleFont("0.3cm")}>{tl("billing.remarks")}:</Typography>
                <DataComment comment={billData.document.remarks} />
              </Box>
            )}

            {/* footer */}
            {showFooter ? (
              <Box
                style={{ ...getSectionStyle(headerColor).footerStyle }}
                sx={{
                  width: "100%",
                  paddingTop: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                {labTestUrl && billDue <= 0 && (
                  <QrFromUrl url={labTestUrl} label={t("billPrint.labTestQrLabel")} />
                )}
                <img src={logo} alt="logo" style={{ height: "35px", marginLeft: "auto" }} />
              </Box>
            ) : (
              <Box width="100%">
                <Box
                  style={{ paddingRight: "10mm" }}
                  mt={2}
                  display="flex"
                  justifyContent="flex-end"
                >
                  {!hideFooterCompletely && <img src={logo} alt="logo" height="35px" />}
                </Box>
                <Box height="2cm" />
              </Box>
            )}
          </div>
        </div>
      </iframe>
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { resourceCentres } = state.resources;
  const rcId = state.userContext.resourceCentreId;

  const getResourceCentre = (id, item) => item.find((rc) => rc.id === id);

  return {
    SPs: state.resources.resourceCentreServiceProviders,
    user: state.userContext.user,
    resourceCentre: getResourceCentre(rcId, resourceCentres) || state.userContext.resourceCentre
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendPrintInfo: (id) => dispatch(registerPrint(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(PrintBill);
